import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableHead, TextField, MenuItem, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, InputAdornment,
  IconButton, Tooltip, Divider, Switch, FormControlLabel, Autocomplete, Collapse,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditIcon from '@mui/icons-material/Edit';
import TuneIcon from '@mui/icons-material/Tune';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { StyledPaper, StyledTableContainer, StyledTableCell, StyledTableRow } from '../assets/styles/tableStyle';
import FilterBar from '../components/common/FilterBar';
import LoadMoreBar from '../components/common/LoadMoreBar';
import PageHelp from '../components/common/PageHelp';
import { useDebounce } from '../hooks/useDebounce';
import { useCursorList, toCursorPage } from '../hooks/useCursorList';
import { useToast } from '../lib/useToast';
import { getApiErrorMessage } from '../lib/toast';
import { useCan } from '../auth/Can';
import { useConfirm } from '../components/feedback/ConfirmDialog';
import {
  getUpiSummary, getDriverUpiList, setDriverUpi, getDriverUpiHistory,
  getPayoutSettings, updatePayoutSettings, getPayoutCities, grantCoins, runUpiVerifyBackfill,
  UpiSummary, DriverUpiRow, UpiChangeLogRow, PayoutSettings,
} from '../services/payoutService';
import { exportDriverUpi } from '../services/exportService';
import PayoutBatchesPanel from './payouts/PayoutBatchesPanel';
import FcmImagePicker from './payouts/FcmImagePicker';

// Mirrors the backend's payoutLogic VPA shape for instant field feedback (server re-validates).
const VPA_RE = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?@[a-z][a-z0-9]{1,63}$/;

const StatCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Paper sx={{ p: 2, flex: '1 1 160px' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
  </Paper>
);

const UPI_COLUMNS: { key: string; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'upi', label: 'UPI ID' },
  { key: 'coins', label: 'Coins (₹)' },
  { key: 'updated', label: 'UPI Updated' },
  { key: 'actions', label: '' },
];

const DriverPayouts: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useCan();
  const canWrite = can('drivers:write');
  const canConfig = can('config:write');

  // ---- Page settings (all AppConfig-backed) ----
  const [settings, setSettings] = useState<PayoutSettings | null>(null);
  const [savingSetting, setSavingSetting] = useState(false); // the switch (instant)
  const [savingVerify, setSavingVerify] = useState(false); // the verify 0/1 dropdown (instant)
  const [savingConfig, setSavingConfig] = useState(false); // account/amount/unit form
  const [editingConfig, setEditingConfig] = useState(false); // account/amount/unit are read-only until Edit
  // Editable settings-form fields (seeded from the loaded settings).
  const [cfgAccount, setCfgAccount] = useState('');
  const [cfgAmount, setCfgAmount] = useState('');
  const [cfgUnit, setCfgUnit] = useState<'rupees' | 'paise'>('rupees');
  const [imagesOpen, setImagesOpen] = useState(false); // Bonus-image block is collapsed by default

  const loadSettings = async () => {
    try {
      const r = await getPayoutSettings();
      setSettings(r.data);
      setCfgAccount(r.data.razorpayAccountNumber || '');
      setCfgAmount(r.data.defaultAmount ? String(r.data.defaultAmount) : '');
      setCfgUnit(r.data.amountUnit || 'rupees');
    } catch {
      /* non-fatal */
    }
  };
  useEffect(() => {
    loadSettings();
  }, []);

  const handleToggleCollection = async (next: boolean) => {
    if (!next) {
      const ok = await confirm({
        title: 'Disable UPI collection?',
        message:
          'Drivers will not be able to add or update their UPI in the app until you re-enable this. Existing UPIs and payouts are unaffected.',
        confirmText: 'Disable',
        destructive: true,
      });
      if (!ok) return;
    }
    const prev = settings;
    setSavingSetting(true);
    setSettings((s) => (s ? { ...s, collectionEnabled: next } : s)); // optimistic
    try {
      await updatePayoutSettings({ collectionEnabled: next });
      toast.success(next ? 'Drivers can now add their UPI' : 'UPI collection disabled');
    } catch (e) {
      setSettings(prev); // revert
      toast.error(getApiErrorMessage(e, 'Failed to update setting'));
    } finally {
      setSavingSetting(false);
    }
  };

  // VPA verification master switch (UPI_VERIFY_ENABLED), saved instantly on change. Upserts via the
  // backend (Prisma upsert sets updatedAt) — raw SQL fails because app_config.updatedAt is NOT NULL.
  const handleToggleVerify = async (next: boolean) => {
    if (!next) {
      const ok = await confirm({
        title: 'Turn off UPI verification?',
        message:
          'The app will hide the whole verify UI and every saved UPI is stored without a bank check — drivers just add & save, and nobody is ever blocked. Turn this off only when no verifier is available (e.g. RazorpayX is not set up). You can turn it back on any time.',
        confirmText: 'Turn off',
        destructive: true,
      });
      if (!ok) return;
    }
    const prev = settings;
    setSavingVerify(true);
    setSettings((s) => (s ? { ...s, verifyEnabled: next } : s)); // optimistic
    try {
      await updatePayoutSettings({ verifyEnabled: next });
      toast.success(next ? 'UPI verification ON' : 'UPI verification OFF — drivers just add & save');
    } catch (e) {
      setSettings(prev); // revert
      toast.error(getApiErrorMessage(e, 'Failed to update verification setting'));
    } finally {
      setSavingVerify(false);
    }
  };

  const handleSaveConfig = async () => {
    const amt = parseFloat(cfgAmount);
    if (cfgAmount.trim() !== '' && (!Number.isFinite(amt) || amt < 0)) {
      toast.error('Default amount must be a non-negative number');
      return;
    }
    const ok = await confirm({
      title: 'Save payout settings?',
      message:
        'These values apply to every export from now on — the amount is prefilled for each driver, and the account/unit go into the RazorpayX file. Changing them affects all payouts you export next. (RazorpayX batches you already uploaded are unaffected.)',
      confirmText: 'Save',
    });
    if (!ok) return;
    setSavingConfig(true);
    try {
      await updatePayoutSettings({
        razorpayAccountNumber: cfgAccount.trim(),
        defaultAmount: cfgAmount.trim() === '' ? 0 : amt,
        amountUnit: cfgUnit,
      });
      await loadSettings();
      setEditingConfig(false);
      toast.success('Payout settings saved');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to save settings'));
    } finally {
      setSavingConfig(false);
    }
  };

  // Revert edits and leave edit mode.
  const cancelEditConfig = () => {
    if (settings) {
      setCfgAccount(settings.razorpayAccountNumber || '');
      setCfgAmount(settings.defaultAmount ? String(settings.defaultAmount) : '');
      setCfgUnit(settings.amountUnit || 'rupees');
    }
    setEditingConfig(false);
  };

  const configDirty =
    !!settings &&
    (cfgAccount.trim() !== (settings.razorpayAccountNumber || '') ||
      (cfgAmount.trim() === '' ? 0 : parseFloat(cfgAmount)) !== settings.defaultAmount ||
      cfgUnit !== settings.amountUnit);

  // ---- Filters (server-side; debounced into the query key) ----
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUpiFilter, setHasUpiFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [currentCity, setCurrentCity] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const debouncedSearch = useDebounce(searchTerm);
  const debouncedCurrentCity = useDebounce(currentCity);
  const debouncedHomeCity = useDebounce(homeCity);

  // City options for the filter dropdowns (distinct driver cities from the backend).
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  useEffect(() => {
    getPayoutCities().then((r) => setCityOptions(r.data.cities || [])).catch(() => {});
  }, []);

  // ---- Summary KPIs ----
  const [summary, setSummary] = useState<UpiSummary | null>(null);
  const loadSummary = async () => {
    try {
      const res = await getUpiSummary({
        currentCity: debouncedCurrentCity || undefined,
        homeCity: debouncedHomeCity || undefined,
      });
      setSummary(res.data);
    } catch {
      // Non-fatal: the table is the primary surface.
    }
  };
  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCurrentCity, debouncedHomeCity]);

  // ---- Cursor-paginated list ----
  const list = useCursorList<DriverUpiRow>({
    key: ['driver-upi', { search: debouncedSearch, hasUpi: hasUpiFilter, currentCity: debouncedCurrentCity, homeCity: debouncedHomeCity }],
    pageSize: 25,
    fetchPage: async ({ cursor, limit }) => {
      const res = await getDriverUpiList({
        cursor,
        limit,
        search: debouncedSearch || undefined,
        hasUpi: hasUpiFilter !== 'all' ? hasUpiFilter : undefined,
        currentCity: debouncedCurrentCity || undefined,
        homeCity: debouncedHomeCity || undefined,
      });
      return toCursorPage<DriverUpiRow>(res.data);
    },
  });

  // ---- Verification backfill (pre-existing UPIs saved before verify-on-save shipped) ----
  const [verifyBusy, setVerifyBusy] = useState(false);
  const handleVerifyBackfill = async () => {
    setVerifyBusy(true);
    try {
      const res = await runUpiVerifyBackfill();
      const s = res.data;
      toast.success(
        `Checked ${s.checked}: ${s.verified} verified, ${s.invalid} invalid, ${s.unavailable} unavailable · ${s.remaining} left unverified`,
      );
      list.refetch();
      loadSummary();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to run UPI verification'));
    } finally {
      setVerifyBusy(false);
    }
  };

  // ---- Coverage export (non-money, plain list) ----
  const [exportBusy, setExportBusy] = useState(false);
  const handleCoverageExport = async () => {
    setExportBusy(true);
    try {
      await exportDriverUpi('plain', {
        currentCity: debouncedCurrentCity || undefined,
        homeCity: debouncedHomeCity || undefined,
      });
      toast.success('Coverage CSV downloaded');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to export driver UPI'));
    } finally {
      setExportBusy(false);
    }
  };

  // ---- Grant coins (bulk by current filter) ----
  const canPay = can('wallet:adjust');
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);

  const handleBulkGrant = async () => {
    const amt = parseInt(grantAmount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive coin amount');
      return;
    }
    const scope = debouncedCurrentCity || debouncedHomeCity
      ? `drivers in the current filter`
      : `ALL drivers`;
    const ok = await confirm({
      title: 'Grant coins?',
      message: `Give ${amt} coins (₹${amt}) to ${scope}. This adds to each driver's earned wallet balance (total − deposits). You can pay it out to their bank via a batch, or they can put it toward a plan. It is not directly withdrawable by the driver.`,
      confirmText: 'Grant coins',
    });
    if (!ok) return;
    setGranting(true);
    try {
      const res: any = await grantCoins({
        amount: amt,
        currentCity: debouncedCurrentCity || undefined,
        homeCity: debouncedHomeCity || undefined,
        note: grantNote.trim() || undefined,
      });
      toast.success(`Granted ${amt} coins to ${res.data.granted} drivers`);
      setGrantOpen(false);
      setGrantAmount('');
      setGrantNote('');
      list.refetch();
      loadSummary();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to grant coins'));
    } finally {
      setGranting(false);
    }
  };

  // ---- Manage-UPI dialog (edit + change history + coins) ----
  const [manageRow, setManageRow] = useState<DriverUpiRow | null>(null);
  const [editVpa, setEditVpa] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<UpiChangeLogRow[] | null>(null);
  const [coinDelta, setCoinDelta] = useState('');
  const [coinBusy, setCoinBusy] = useState(false);

  const openManage = async (row: DriverUpiRow) => {
    setManageRow(row);
    setEditVpa(row.payoutUpiVpa || '');
    setCoinDelta('');
    setHistory(null);
    try {
      const res = await getDriverUpiHistory(row.id);
      setHistory(res.data?.data ?? []);
    } catch {
      setHistory([]);
    }
  };

  const handleCoinAdjust = async () => {
    if (!manageRow) return;
    const delta = parseInt(coinDelta, 10);
    if (!Number.isFinite(delta) || delta === 0) {
      toast.error('Enter a non-zero amount (use a minus sign to revoke)');
      return;
    }
    setCoinBusy(true);
    try {
      const res: any = await grantCoins({ driverId: manageRow.id, amount: delta });
      toast.success(`Coins updated → ${res.data.balance}`);
      setManageRow({ ...manageRow, rewardCoins: res.data.balance });
      setCoinDelta('');
      list.refetch();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update coins'));
    } finally {
      setCoinBusy(false);
    }
  };

  const normalizedEdit = editVpa.trim().toLowerCase();
  const editValid = normalizedEdit === '' || VPA_RE.test(normalizedEdit);

  const handleSaveVpa = async () => {
    if (!manageRow) return;
    setSaving(true);
    try {
      const res = await setDriverUpi(manageRow.id, normalizedEdit);
      const { verified, holderName } = res.data ?? {};
      toast.success(
        !normalizedEdit
          ? 'Driver UPI cleared'
          : verified
            ? `Driver UPI verified & updated${holderName ? ` — belongs to ${holderName}` : ''}`
            : 'Driver UPI updated (verification unavailable — saved unverified)',
      );
      setManageRow(null);
      list.refetch();
      loadSummary();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update driver UPI'));
    } finally {
      setSaving(false);
    }
  };

  const hasFilters = !!searchTerm || hasUpiFilter !== 'all' || !!currentCity || !!homeCity;
  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <Box sx={{ maxWidth: '98vw', width: '100%', my: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <AccountBalanceWalletIcon fontSize="large" color="primary" />
        <Typography variant="h4" fontWeight="bold">Driver Payouts — UPI</Typography>
        <PageHelp k="driverPayouts" size="medium" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drivers add their UPI ID in the app; you grant them reward coins (1 coin = ₹1) and pay via a
        RazorpayX batch — coins turn into cash only when you mark a batch paid. Open the “?” on
        “Reward payouts” for the full flow.
      </Typography>

      {/* Payout settings — all AppConfig-backed, saved directly from here */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <TuneIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Payout settings
          </Typography>
          <Tooltip title={canConfig ? '' : 'Requires the config:write permission'} disableHoverListener={canConfig}>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={settings?.collectionEnabled ?? true}
                  onChange={(e) => handleToggleCollection(e.target.checked)}
                  disabled={!canConfig || savingSetting || settings === null}
                />
              }
              label={
                (settings?.collectionEnabled ?? true)
                  ? 'Driver UPI collection: ON'
                  : 'Driver UPI collection: OFF'
              }
            />
          </Tooltip>
        </Box>

        {/* VPA verification master switch — a 0/1 dropdown, saved instantly on change (upsert). */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title={canConfig ? '' : 'Requires the config:write permission'} disableHoverListener={canConfig}>
            <TextField
              select
              label="UPI verification"
              size="small"
              value={settings ? (settings.verifyEnabled ? '1' : '0') : '1'}
              onChange={(e) => handleToggleVerify(e.target.value === '1')}
              disabled={!canConfig || savingVerify || settings === null}
              sx={{ width: 220 }}
            >
              <MenuItem value="1">1 — Verification ON</MenuItem>
              <MenuItem value="0">0 — Verification OFF</MenuItem>
            </TextField>
          </Tooltip>
          {savingVerify && <CircularProgress size={16} />}
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 260, maxWidth: 560 }}>
            <strong>1</strong> = drivers verify their UPI in the app (“Verify &amp; Save”; a UPI ID that
            doesn’t exist is blocked). <strong>0</strong> = verification off: the app hides the verify UI
            and drivers just add &amp; save — nobody is ever blocked. Set <strong>0</strong> when no
            verifier is available (e.g. RazorpayX isn’t set up).
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start',
            cursor: !editingConfig && canConfig && settings !== null ? 'pointer' : 'default',
          }}
          onClick={() => { if (!editingConfig && canConfig && settings !== null) setEditingConfig(true); }}
        >
          <TextField
            label="RazorpayX account number"
            size="small"
            value={cfgAccount}
            onChange={(e) => setCfgAccount(e.target.value)}
            disabled={!editingConfig || !canConfig || settings === null}
            sx={{ width: 240 }}
            error={editingConfig && settings !== null && !cfgAccount.trim()}
            helperText={!cfgAccount.trim() ? 'Not set — exports will have a blank account column' : 'Prefilled into every export'}
          />
          <TextField
            label="Default payout amount"
            size="small"
            type="number"
            value={cfgAmount}
            onChange={(e) => setCfgAmount(e.target.value)}
            disabled={!editingConfig || !canConfig || settings === null}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            sx={{ width: 200 }}
            helperText="Prefilled on every row (0 = blank)"
          />
          <TextField
            select label="Amount unit" size="small" value={cfgUnit}
            onChange={(e) => setCfgUnit(e.target.value as 'rupees' | 'paise')}
            disabled={!editingConfig || !canConfig || settings === null}
            sx={{ width: 150 }} helperText="Match your template"
          >
            <MenuItem value="rupees">Rupees</MenuItem>
            <MenuItem value="paise">Paise</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
            {!editingConfig ? (
              <Tooltip title={canConfig ? '' : 'Requires the config:write permission'} disableHoverListener={canConfig}>
                <span>
                  <Button
                    variant="outlined" startIcon={<EditIcon />}
                    onClick={() => setEditingConfig(true)}
                    disabled={!canConfig || settings === null}
                  >
                    Edit
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={!canConfig || savingConfig || !configDirty}
                  startIcon={savingConfig ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {savingConfig ? 'Saving…' : 'Save settings'}
                </Button>
                <Button color="inherit" onClick={cancelEditConfig} disabled={savingConfig}>
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Stored in AppConfig. Click <strong>Edit</strong> to change; set the amount to ₹10 this month,
          ₹50 next month, etc. — each export prefills that amount for every driver.
        </Typography>

        {/* Bonus-push artwork — big banner + small icon shown on the "Bonus sent" notification.
            Header + description stay visible; the image pickers collapse so the panel can be minimized. */}
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, cursor: 'pointer' }}
          onClick={() => setImagesOpen((o) => !o)}
        >
          <ImageIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bonus notification images</Typography>
          {(() => {
            const parts = [settings?.bannerUrl ? 'banner' : null, settings?.iconUrl ? 'icon' : null].filter(Boolean);
            return (
              <Typography variant="caption" color={parts.length ? 'success.main' : 'text.secondary'} sx={{ flex: 1 }}>
                {parts.length ? `${parts.join(' + ')} set` : 'none set'}
              </Typography>
            );
          })()}
          <IconButton size="small" aria-label={imagesOpen ? 'Collapse' : 'Expand'} onClick={(e) => { e.stopPropagation(); setImagesOpen((o) => !o); }}>
            {imagesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Optional. The “💰 Bonus sent” push shows the big banner (expanded) with the small icon as its
          thumbnail. Leave empty for a plain text push. Upload, preview, then <strong>Save</strong>.
        </Typography>
        <Collapse in={imagesOpen} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', pt: 0.5 }}>
            <FcmImagePicker
              kind="banner"
              label="Big banner (BigPicture)"
              hint="Wide image, ~2:1. Shown when the notification is expanded."
              currentUrl={settings?.bannerUrl || ''}
              canEdit={canConfig && settings !== null}
              previewWidth={260}
              previewHeight={130}
              onSaved={loadSettings}
            />
            <FcmImagePicker
              kind="icon"
              label="Small icon (thumbnail)"
              hint="Square logo/coin. Shown on the right of the collapsed notification."
              currentUrl={settings?.iconUrl || ''}
              canEdit={canConfig && settings !== null}
              previewWidth={96}
              previewHeight={96}
              onSaved={loadSettings}
            />
          </Box>
        </Collapse>
      </Paper>

      {/* KPI cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <StatCard label="Active drivers" value={summary ? String(summary.totalDrivers) : '…'} />
        <StatCard
          label="With UPI"
          value={summary ? `${summary.withUpi} (${summary.coveragePct}%)` : '…'}
          color="#2e7d32"
        />
        <StatCard
          label="Verified UPI"
          value={summary ? `${summary.verifiedUpi ?? 0} / ${summary.withUpi}` : '…'}
          color="#2e7d32"
        />
        <StatCard label="Missing UPI" value={summary ? String(summary.withoutUpi) : '…'} color="#ed6c02" />
        <StatCard label="Changes (7d)" value={summary ? String(summary.changes7d) : '…'} />
      </Box>

      {/* Reward payouts — coins → cash via batches */}
      <PayoutBatchesPanel
        currentCity={debouncedCurrentCity}
        homeCity={debouncedHomeCity}
        defaultAmount={settings?.defaultAmount}
        canPay={canPay}
        onCoinsChanged={() => { list.refetch(); loadSummary(); }}
      />

      {/* Worklist table */}
      <StyledPaper sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <FilterBar
          title="Drivers"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Phone, name or UPI…' }}
          hasActiveFilters={hasFilters}
          onClear={() => { setSearchTerm(''); setHasUpiFilter('all'); setCurrentCity(''); setHomeCity(''); }}
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canPay && (
                <Button size="small" variant="outlined" onClick={() => setGrantOpen(true)}>
                  Grant coins
                </Button>
              )}
              {canWrite && (
                <Tooltip
                  title={
                    settings?.verifyEnabled === false
                      ? 'UPI verification is turned OFF — turn it on above to run a backfill.'
                      : 'Check every unverified saved UPI against the bank. ~200 per run — rerun until 0 remain.'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VerifiedIcon fontSize="small" />}
                      onClick={handleVerifyBackfill}
                      disabled={verifyBusy || settings?.verifyEnabled === false}
                    >
                      {verifyBusy ? 'Verifying…' : 'Verify UPIs'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button size="small" variant="text" startIcon={<DownloadIcon fontSize="small" />} onClick={handleCoverageExport} disabled={exportBusy}>
                {exportBusy ? 'Exporting…' : 'Coverage CSV'}
              </Button>
            </Box>
          }
        >
          <TextField
            select size="small" label="UPI status" value={hasUpiFilter}
            onChange={(e) => setHasUpiFilter(e.target.value as 'all' | 'yes' | 'no')} sx={{ minWidth: 140 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="yes">Has UPI</MenuItem>
            <MenuItem value="no">Missing UPI</MenuItem>
          </TextField>
          <Autocomplete
            freeSolo size="small" options={cityOptions}
            inputValue={currentCity}
            onInputChange={(_, v) => setCurrentCity(v)}
            sx={{ minWidth: 170 }}
            renderInput={(params) => <TextField {...params} label="Current city" />}
          />
          <Autocomplete
            freeSolo size="small" options={cityOptions}
            inputValue={homeCity}
            onInputChange={(_, v) => setHomeCity(v)}
            sx={{ minWidth: 170 }}
            renderInput={(params) => <TextField {...params} label="Home city" />}
          />
        </FilterBar>

        <StyledTableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <StyledTableRow>
                {UPI_COLUMNS.map((col) => (
                  <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {list.isLoading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={UPI_COLUMNS.length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </StyledTableCell>
                </StyledTableRow>
              ) : list.rows.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={UPI_COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No drivers match these filters
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                list.rows.map((row) => (
                  <StyledTableRow
                    key={row.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/driver/${row.id}`)}
                  >
                    <StyledTableCell>{row.id}</StyledTableCell>
                    <StyledTableCell>{row.user?.name || '—'}</StyledTableCell>
                    <StyledTableCell>{row.user?.phone || '—'}</StyledTableCell>
                    <StyledTableCell>{row.user?.currentCity || row.user?.homeCity || '—'}</StyledTableCell>
                    <StyledTableCell sx={{ maxWidth: 260 }}>
                      {row.payoutUpiVpa ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {row.payoutUpiVpa}
                          </Typography>
                          {row.payoutUpiVerifiedAt && (
                            <Tooltip title={`Verified${row.payoutUpiHolderName ? ` — ${row.payoutUpiHolderName}` : ''} (${new Date(row.payoutUpiVerifiedAt).toLocaleDateString()})`}>
                              <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            </Tooltip>
                          )}
                          {!row.payoutUpiVerifiedAt && row.payoutUpiVerifyFailedAt && (
                            <Tooltip title={`This UPI ID does not exist (checked ${new Date(row.payoutUpiVerifyFailedAt).toLocaleDateString()}) — payouts to it will bounce. Ask the driver to fix it.`}>
                              <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Chip label="missing" size="small" color="warning" variant="outlined" />
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography component="span" sx={{ fontWeight: 600, color: (row.rewardCoins ?? 0) > 0 ? '#2e7d32' : 'text.secondary' }}>
                        ₹{row.rewardCoins ?? 0}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>{fmtDate(row.payoutUpiUpdatedAt)}</StyledTableCell>
                    <StyledTableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={canWrite ? 'Edit UPI / view history' : 'View change history'}>
                        <IconButton size="small" onClick={() => openManage(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <LoadMoreBar
          count={list.rows.length}
          hasMore={list.hasMore}
          loading={list.isLoadingMore}
          onLoadMore={list.loadMore}
        />
      </StyledPaper>

      {/* Manage-UPI dialog */}
      <Dialog open={!!manageRow} onClose={() => !saving && setManageRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            UPI — {manageRow?.user?.name || `Driver #${manageRow?.id}`}
            <PageHelp k="driverPayoutsManage" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {manageRow?.user?.phone}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth size="small" label="UPI ID (VPA)"
            value={editVpa} onChange={(e) => setEditVpa(e.target.value)}
            placeholder="name@bank" disabled={!canWrite || saving}
            error={!editValid}
            helperText={
              !editValid
                ? 'Invalid format — expected name@bank (e.g. 9741113191@ybl)'
                : canWrite
                  ? 'Leave empty and save to clear. Every change is journaled.'
                  : 'You need drivers:write to edit.'
            }
            InputProps={{ sx: { fontFamily: 'monospace' } }}
            sx={{ my: 1 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Reward coins — balance ₹{manageRow?.rewardCoins ?? 0}
          </Typography>
          {canPay ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
              <TextField
                size="small" type="number" label="Grant / revoke"
                value={coinDelta} onChange={(e) => setCoinDelta(e.target.value)}
                placeholder="e.g. 10 or -5" sx={{ width: 170 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                helperText="Use a minus sign to revoke"
              />
              <Button variant="outlined" sx={{ mt: 0.5 }} onClick={handleCoinAdjust} disabled={coinBusy}>
                {coinBusy ? '…' : 'Apply'}
              </Button>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">You need wallet:adjust to change coins.</Typography>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Change history</Typography>
          {history === null ? (
            <CircularProgress size={18} />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No changes recorded.</Typography>
          ) : (
            history.map((h) => (
              <Box key={h.id} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                  {h.oldVpa || '(none)'} → {h.newVpa || '(cleared)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(h.createdAt).toLocaleString()} · {h.source === 'admin' ? `admin #${h.actorId ?? '?'}` : 'driver app'}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManageRow(null)} disabled={saving} color="inherit">Close</Button>
          {canWrite && (
            <Button
              variant="contained" onClick={handleSaveVpa}
              disabled={saving || !editValid || normalizedEdit === (manageRow?.payoutUpiVpa || '')}
            >
              {saving ? <CircularProgress size={22} /> : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk grant-coins dialog */}
      <Dialog open={grantOpen} onClose={() => !granting && setGrantOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Grant coins</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grants coins to{' '}
            <strong>
              {debouncedCurrentCity || debouncedHomeCity
                ? `drivers in the current filter${debouncedCurrentCity ? ` · current: ${debouncedCurrentCity}` : ''}${debouncedHomeCity ? ` · home: ${debouncedHomeCity}` : ''}`
                : 'ALL active drivers'}
            </strong>
            . 1 coin = ₹1; this adds to each driver's earned wallet balance (paid to their bank via a
            batch, or usable toward a plan) — not directly withdrawable by the driver.
          </Typography>
          <TextField
            autoFocus fullWidth size="small" type="number" label="Coins per driver"
            value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Note (optional)"
            value={grantNote} onChange={(e) => setGrantNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setGrantOpen(false)} disabled={granting}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkGrant} disabled={granting}>
            {granting ? <CircularProgress size={22} /> : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverPayouts;
